<template>
    <v-container class="pa-5" fluid>
        <page-title>Zamawianie dokumentów</page-title>

        <v-row align="center">
            <v-col cols="12" sm="4">
                <v-btn
                    color="secondary"
                    medium
                    class="mx-0"
                    data-sentry-label="Zamów dokument"
                    @click="createDocument"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Zamów dokument
                </v-btn>
            </v-col>
            <v-col
                v-if="canDisplayFilters"
                cols="12"
                sm="8"
                class="d-flex justify-end align-center"
            >
                <v-btn
                    small
                    fab
                    color="grey darken-1"
                    dark
                    class="filters-toggle-btn elevation-2"
                    @click="toggleFilters"
                >
                    <v-icon>
                        {{ filtersVisible ? 'mdi-close' : 'mdi-filter-variant' }}
                    </v-icon>
                </v-btn>
            </v-col>
        </v-row>
        <v-row v-if="filtersVisible && canDisplayFilters">
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row dense>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-select
                                v-model="filters.type_id"
                                :items="types"
                                :loading="typesLoading"
                                item-value="id"
                                item-text="name"
                                label="Rodzaj dokumentu"
                                placeholder="Wybierz rodzaj"
                                no-data-text="Brak danych"
                                hide-details
                                solo
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-select
                                v-model="filters.status_id"
                                :items="statuses"
                                :loading="statusesLoading"
                                item-value="id"
                                item-text="name"
                                label="Status"
                                placeholder="Wybierz status"
                                no-data-text="Brak danych"
                                hide-details
                                solo
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-select
                                v-model="filters.productionDepartment_id"
                                :items="productionDepartments"
                                :loading="productionDepartmentsLoading"
                                item-value="id"
                                item-text="name"
                                label="Dział produkcji"
                                placeholder="Wybierz dział"
                                no-data-text="Brak danych"
                                hide-details
                                solo
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-autocomplete
                                v-model="filters.authors"
                                :items="authorsOptions"
                                :search-input.sync="authorsSearch"
                                :loading="authorsLoading"
                                item-value="id"
                                item-text="name"
                                label="Autorzy"
                                placeholder="Wyszukaj autora"
                                no-data-text="Brak wyników"
                                persistent-placeholder
                                hide-details
                                solo
                                multiple
                                chips
                                small-chips
                                clearable
                            />
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-menu
                                v-model="createdAtGteMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.createdAt__gte"
                                        label="Data utworzenia od"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.createdAt__gte"
                                    first-day-of-week="1"
                                    @input="createdAtGteMenu = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" lg="3">
                            <v-menu
                                v-model="createdAtLteMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.createdAt__lte"
                                        label="Data utworzenia do"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.createdAt__lte"
                                    first-day-of-week="1"
                                    @input="createdAtLteMenu = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            sm="6"
                            md="4"
                            lg="3"
                            class="d-flex align-center"
                        >
                            <v-btn
                                color="primary"
                                class="ma-0 mr-2"
                                :loading="documentsLoading"
                                :disabled="documentsLoading"
                                @click="applyFilters"
                            >
                                Zastosuj filtry
                            </v-btn>
                            <v-btn
                                text
                                class="ma-0"
                                :disabled="documentsLoading"
                                @click="clearFilters"
                            >
                                Wyczyść
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="documents"
                        :loading="documentsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :mobile-breakpoint="0"
                        @pagination="updatePagination"
                    >
                        <template #item.type="{item}">
                            {{ item.type.name }}
                        </template>
                        <template #item.productionDepartment="{item}">
                            {{ item.productionDepartment.name }}
                        </template>
                        <template #item.status="{item}">
                            {{ item.status.name }}
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.createdBy="{item}">
                            {{ parseValue(item.createdBy.first_name + ' ' + item.createdBy.last_name) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="openDocument(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import router from '@/router'
import {errorHandler} from '@/utils/errorHandler'
import filters from '@/mixins/dataTableFilters'
import dateFormatter from '@/mixins/dateFormatter'
import { hasUserPermission } from '@/utils/permissionsHandler'

const createDefaultFilters = () => ({
    type_id: null,
    status_id: null,
    productionDepartment_id: null,
    createdAt__gte: null,
    createdAt__lte: null,
    authors: []
})

const toAuthorOption = (user = {}) => ({
    ...user,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username
})

const formatFilterValue = (key, value) => {
    if (typeof value !== 'string') {
        return value
    }

    if (key === 'createdAt__gte') {
        return value.includes('T') ? value : `${value}T00:00:00Z`
    }

    if (key === 'createdAt__lte') {
        return value.includes('T') ? value : `${value}T23:59:59Z`
    }

    return value
}

export default {
    name: 'Documents',
    components: {
        PageTitle,
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter
    ],
    data() {
        return {
            createdAtGteMenu: false,
            createdAtLteMenu: false,
            filtersVisible: false,
            authorsOptions: [],
            authorsLoading: false,
            authorsSearch: null,
            authorsSearchTimeout: null,
            skipNextPaginationFetch: false,
            filters: createDefaultFilters(),
            headers: [
                {
                    text: 'Numer zgłoszenia',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Rodzaj dokumentu',
                    value: 'type',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Dział',
                    value: 'productionDepartment',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'status',
                    value: 'status',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Proponowany termin',
                    value: 'proposedDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Zgłaszający',
                    value: 'createdBy',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'right',
                    width: 100
                }
            ]
        }
    },

    computed: {
        documents() {
            return this.$store.state.documents.documentsOrders.documents
        },
        documentsLoading() {
            return this.$store.state.documents.documentsOrders.documentsLoading
        },
        types() {
            return this.$store.state.documents.documentsOrders.types
        },
        typesLoading() {
            return this.$store.state.documents.documentsOrders.typesLoading
        },
        statuses() {
            return this.$store.state.documents.documentsOrders.statuses
        },
        statusesLoading() {
            return this.$store.state.documents.documentsOrders.statusesLoading
        },
        productionDepartments() {
            return this.$store.state.documents.documentsOrders.productionDepartments
        },
        productionDepartmentsLoading() {
            return this.$store.state.documents.documentsOrders.productionDepartmentsLoading
        },
        canDisplayFilters() {
            return hasUserPermission('docs_order_admin_read')
        },
        filterQuery() {
            return Object.keys(this.filters).reduce((query, key) => {
                const value = this.filters[key]

                if (key === 'authors') {
                    return value.length ? query.concat('&', `${key}=${value.join(',')}`) : query
                }

                const formattedValue = formatFilterValue(key, value)

                return formattedValue !== null && formattedValue !== undefined && formattedValue !== '' ?
                    query.concat('&', `${key}=${formattedValue}`) :
                    query
            }, '')
        }
    },
    watch: {
        authorsSearch(value) {
            if (value === null) {
                return
            }

            if (this.authorsSearchTimeout) {
                clearTimeout(this.authorsSearchTimeout)
            }

            if (!value || value.length < 3) {
                this.authorsLoading = false

                return
            }

            this.authorsLoading = true
            this.authorsSearchTimeout = setTimeout(() => {
                this.fetchAuthors(value)
            }, 400)
        }
    },

    mounted() {
        Promise.all([
            this.$store.dispatch('documents/getTypes'),
            this.$store.dispatch('documents/getStatuses'),
            this.$store.dispatch('documents/getProductionDepartmentsForDocuments')
        ])
            .catch(() => {})
            .finally(() => {
                this.getDocuments()
            })
    },
    beforeDestroy() {
        if (this.authorsSearchTimeout) {
            clearTimeout(this.authorsSearchTimeout)
        }
    },
    methods: {
        createDocument() {
            router.push({ name: 'documentsDocumentsCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getDocuments() {
            this.$store.dispatch('documents/getDocuments', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.skipNextPaginationFetch = false
                })
        },
        openDocument(document) {
            router.push({ name: 'documentsDocumentsEdit', params: { id: document.id } })
                .catch(error => {
                    errorHandler(error)
                })
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (this.skipNextPaginationFetch) {
                this.clearPaginationPending()

                return
            }
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getDocuments()
            }
            this.clearPaginationPending()
        },
        parseValue(value) {
            return value ? value.trim() : '-'
        },
        toggleFilters() {
            this.filtersVisible = !this.filtersVisible
        },
        applyFilters() {
            this.skipNextPaginationFetch = true
            this.resetPagination()
            this.getDocuments()
        },
        clearFilters() {
            this.skipNextPaginationFetch = true
            this.filters = createDefaultFilters()
            this.resetPagination()
            this.getDocuments()
        },
        fetchAuthors(query = '') {
            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    const { results = [] } = response.data
                    
                    this.mergeAuthorsOptions(results)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.authorsLoading = false
                })
        },
        mergeAuthorsOptions(newAuthors = []) {
            const authorsMap = new Map(this.authorsOptions.map(author => [author.id, author]))

            newAuthors.filter(Boolean).forEach(author => {
                authorsMap.set(author.id, toAuthorOption(author))
            })

            this.authorsOptions = Array.from(authorsMap.values())
        },
    },
}
</script>
